<template>
  <div class="project-hero-media" :class="{ 'project-hero-media--page-sizing': pageSizing }">
    <div class="project-hero-media__banner" :style="bannerStyle" @click="openBannerPicker">
      <div class="project-hero-media__overlay"></div>
      <div class="project-hero-media__top">
        <span class="project-hero-media__age" :style="ageRatingStyle">{{ ageRatingLabel }}</span>
        <v-btn
          small
          dark
          class="zinnes-primary-btn project-hero-media__banner-action"
          :disabled="disabled"
          @click.stop="openBannerPicker"
        >
          {{ displayedBanner ? 'Trocar banner' : 'Adicionar banner' }}
        </v-btn>
      </div>
      <span v-if="!displayedBanner" class="project-hero-media__banner-empty">
        Banner da pagina da obra
      </span>
    </div>

    <div class="project-hero-media__body">
      <div class="project-hero-media__cover-stack">
        <button
          type="button"
          class="project-hero-media__cover"
          :class="{ 'project-hero-media__cover--empty': !displayedCover }"
          :disabled="disabled"
          @click="openCoverPicker"
        >
          <img v-if="displayedCover" :src="displayedCover" alt="Preview da capa da obra" />
          <span v-else>Capa da obra</span>
        </button>
        <v-btn
          small
          dark
          color="var(--zinnes-primary)"
          class="project-hero-media__cover-action"
          :disabled="disabled"
          @click="openCoverPicker"
        >
          {{ displayedCover ? 'Trocar capa' : 'Adicionar capa' }}
        </v-btn>
      </div>

      <div class="project-hero-media__content">
        <h2>{{ title || 'Nome da obra' }}</h2>
        <div class="project-hero-media__tags">
          <span v-for="item in visibleMetadata" :key="item">{{ item }}</span>
        </div>
        <p>{{ description || 'A descricao da obra aparecera aqui enquanto voce preenche o cadastro.' }}</p>
        <div class="project-hero-media__actions">
          <v-btn small dark class="zinnes-primary-btn" disabled>Iniciar leitura</v-btn>
          <span>Preview do topo publico da obra</span>
        </div>
      </div>
    </div>

    <input
      ref="coverInput"
      class="project-hero-media__input"
      type="file"
      accept="image/png, image/jpeg, image/webp, image/gif"
      :disabled="disabled"
      @change="onCoverSelected"
    />
    <input
      ref="bannerInput"
      class="project-hero-media__input"
      type="file"
      accept="image/png, image/jpeg, image/webp, image/gif"
      :disabled="disabled"
      @change="onBannerSelected"
    />

    <div class="project-hero-media__help">
      Capa vertical para cards e capa sobreposta. Banner da obra em 1600x920px. PNG, JPG, WebP ou GIF, ate 6MB.
    </div>
  </div>
</template>

<script>
const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_IMAGE_SIZE = 6 * 1024 * 1024;

export default {
  name: 'ProjectHeroMediaUpload',
  props: {
    coverFile: {
      type: [Object, String],
      default: null,
    },
    currentCover: {
      type: String,
      default: '',
    },
    bannerFile: {
      type: Object,
      default: null,
    },
    currentBanner: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    metadata: {
      type: Array,
      default: () => [],
    },
    ageRating: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    pageSizing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      coverPreview: '',
      bannerPreview: '',
    };
  },
  computed: {
    visibleMetadata() {
      return this.metadata.filter(Boolean);
    },
    displayedCover() {
      return this.coverPreview || this.currentCover || '';
    },
    displayedBanner() {
      return this.bannerPreview || this.currentBanner || '';
    },
    bannerStyle() {
      return this.displayedBanner ? { backgroundImage: `url("${this.displayedBanner}")` } : {};
    },
    ageRatingLabel() {
      return (this.ageRating && this.ageRating.rating) || 'Livre';
    },
    ageRatingStyle() {
      const age = (this.ageRating && this.ageRating.age) || 10;
      return {
        backgroundColor: this.ratingColor(age),
        color: age === 12 ? '#000' : '#fff',
      };
    },
  },
  watch: {
    coverFile(newValue) {
      if (!this.isFile(newValue)) {
        this.coverPreview = '';
        if (this.$refs.coverInput) this.$refs.coverInput.value = '';
      }
    },
    bannerFile(newValue) {
      if (!this.isFile(newValue)) {
        this.bannerPreview = '';
        if (this.$refs.bannerInput) this.$refs.bannerInput.value = '';
      }
    },
  },
  methods: {
    openCoverPicker() {
      if (!this.disabled && this.$refs.coverInput) {
        this.$refs.coverInput.click();
      }
    },
    openBannerPicker() {
      if (!this.disabled && this.$refs.bannerInput) {
        this.$refs.bannerInput.click();
      }
    },
    validateFile(file, label) {
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        return `Envie ${label} em PNG, JPG, WebP ou GIF.`;
      }

      if (file.size > MAX_IMAGE_SIZE) {
        return `${label} deve ter no maximo 6MB.`;
      }

      return '';
    },
    readPreview(file, callback) {
      const reader = new FileReader();
      reader.onload = (event) => callback(event.target.result);
      reader.readAsDataURL(file);
    },
    isFile(value) {
      return Object.prototype.toString.call(value) === '[object File]';
    },
    onCoverSelected(event) {
      const file = event.target.files[0];
      if (!file) return;

      const error = this.validateFile(file, 'A capa');
      if (error) {
        this.$emit('error', error);
        this.$emit('cover-selected', null);
        event.target.value = '';
        return;
      }

      this.readPreview(file, (src) => {
        this.coverPreview = src;
      });
      this.$emit('cover-selected', file);
    },
    onBannerSelected(event) {
      const file = event.target.files[0];
      if (!file) return;

      const error = this.validateFile(file, 'O banner');
      if (error) {
        this.$emit('error', error);
        this.$emit('banner-selected', null);
        event.target.value = '';
        return;
      }

      this.readPreview(file, (src) => {
        this.bannerPreview = src;
      });
      this.$emit('banner-selected', file);
    },
    ratingColor(age) {
      const colors = {
        10: '#2e7d32',
        12: '#fdd835',
        14: '#f57c00',
        16: '#c62828',
        18: '#111111',
      };
      return colors[Number(age)] || '#2e7d32';
    },
  },
};
</script>

<style scoped>
.project-hero-media {
  background: #1e1e1e;
  border: 1px solid #4b4a4a;
  border-radius: 8px;
  box-sizing: border-box;
  color: #ffffff;
  overflow: hidden;
  width: 100%;
}

.project-hero-media,
.project-hero-media * {
  box-sizing: border-box;
}

.project-hero-media__banner {
  align-items: center;
  aspect-ratio: 40 / 23;
  background: var(--zinnes-primary);
  background-position: center;
  background-size: cover;
  cursor: pointer;
  display: flex;
  justify-content: center;
  min-height: 150px;
  position: relative;
}

.project-hero-media__overlay {
  background: linear-gradient(0deg, rgba(30, 30, 30, 0.88) 0%, rgba(30, 30, 30, 0.52) 44%, rgba(30, 30, 30, 0.16) 78%, rgba(0, 0, 0, 0) 100%);
  inset: 0;
  position: absolute;
}

.project-hero-media__top {
  align-items: flex-start;
  display: flex;
  gap: 8px;
  inset: 0 0 auto;
  justify-content: space-between;
  padding: 14px;
  position: absolute;
  z-index: 1;
}

.project-hero-media__age {
  border-radius: 8px;
  display: inline-flex;
  font-size: 13px;
  font-weight: 800;
  line-height: 1;
  padding: 6px 9px;
}

.project-hero-media__banner-empty {
  color: rgba(255, 255, 255, 0.82);
  font-size: 13px;
  font-weight: 700;
  position: relative;
  z-index: 1;
}

.project-hero-media__body {
  display: grid;
  gap: 18px;
  grid-template-columns: 180px minmax(0, 1fr);
  margin-top: -64px;
  padding: 0 24px 22px;
  position: relative;
  z-index: 2;
}

.project-hero-media__cover-stack {
  display: grid;
  gap: 8px;
  justify-items: center;
  min-width: 0;
  position: relative;
}

.project-hero-media__cover {
  aspect-ratio: 2 / 3;
  background: #2a2a2a;
  border: 3px solid #1e1e1e;
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.48);
  color: #d9d9d9;
  cursor: pointer;
  display: grid;
  overflow: hidden;
  padding: 0;
  place-items: center;
  width: 100%;
}

.project-hero-media__cover img {
  height: 100%;
  object-fit: cover;
  object-position: center;
  width: 100%;
}

.project-hero-media__cover--empty {
  padding: 12px;
}

.project-hero-media__cover-action.v-btn {
  left: 50%;
  margin: 0;
  position: absolute;
  top: 8px;
  transform: translateX(-50%);
  z-index: 3;
}

.project-hero-media--page-sizing .project-hero-media__banner {
  aspect-ratio: 16 / 5.2;
}

.project-hero-media--page-sizing .project-hero-media__body {
  gap: 20px;
  grid-template-columns: 170px minmax(0, 1fr);
  margin-top: -83px;
  padding: 0 32px 24px;
}

.project-hero-media--page-sizing .project-hero-media__cover-stack,
.project-hero-media--page-sizing .project-hero-media__cover {
  width: 170px;
}

.project-hero-media__content {
  align-self: end;
  display: grid;
  gap: 10px;
  min-width: 0;
  padding-bottom: 4px;
}

.project-hero-media__content h2 {
  color: #ffffff;
  font-size: clamp(24px, 3vw, 36px);
  line-height: 1.08;
  margin: 0;
  overflow-wrap: anywhere;
}

.project-hero-media__content p {
  color: #d9d9d9 !important;
  display: -webkit-box;
  font-size: 13px;
  line-height: 1.45;
  margin: 0;
  max-width: 620px;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.project-hero-media__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.project-hero-media__tags span {
  background: #0a0a0a;
  border: 1px solid #4b4a4a;
  border-radius: 999px;
  color: #d9d9d9;
  font-size: 12px;
  padding: 5px 9px;
}

.project-hero-media__actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.project-hero-media__actions span,
.project-hero-media__help {
  color: #cccccc;
  font-size: 12px;
}

.project-hero-media__help {
  border-top: 1px solid #4b4a4a;
  padding: 10px 14px;
}

.project-hero-media__input {
  display: none;
}

.project-hero-media--page-sizing .project-hero-media__content h2 {
  font-size: clamp(28px, 3.6vw, 44px);
}

@media (max-width: 768px) {
  .project-hero-media__body {
    gap: 14px;
    grid-template-columns: 136px minmax(0, 1fr);
    margin-top: -48px;
    padding: 0 14px 18px;
  }

  .project-hero-media--page-sizing .project-hero-media__body {
    grid-template-columns: 136px minmax(0, 1fr);
    margin-top: -66px;
    padding: 0 14px 18px;
  }

  .project-hero-media--page-sizing .project-hero-media__cover-stack,
  .project-hero-media--page-sizing .project-hero-media__cover {
    width: 136px;
  }
}

@media (max-width: 600px) {
  .project-hero-media__body {
    grid-template-columns: minmax(120px, min(44vw, 152px));
    justify-content: center;
    text-align: center;
  }

  .project-hero-media__content,
  .project-hero-media__tags,
  .project-hero-media__actions {
    justify-content: center;
  }

  .project-hero-media--page-sizing .project-hero-media__body {
    grid-template-columns: minmax(120px, min(44vw, 152px));
    margin-top: -66px;
  }

  .project-hero-media--page-sizing .project-hero-media__cover-stack,
  .project-hero-media--page-sizing .project-hero-media__cover {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .project-hero-media__banner {
    min-height: 132px;
  }

  .project-hero-media--page-sizing .project-hero-media__banner {
    aspect-ratio: 16 / 6.2;
  }

  .project-hero-media__top {
    padding: 10px 16px;
  }

  .project-hero-media__banner-action.v-btn {
    align-self: flex-start;
    flex: none;
    font-size: 10px;
    letter-spacing: 0.6px;
    margin: 0 0 0 auto;
    max-width: calc(100% - 64px);
    min-width: 0;
    padding-inline: 8px;
    position: static;
  }

  .project-hero-media__content h2,
  .project-hero-media--page-sizing .project-hero-media__content h2 {
    font-size: clamp(22px, 6.2vw, 28px);
  }
}

@media (max-width: 430px) {
  .project-hero-media__body,
  .project-hero-media--page-sizing .project-hero-media__body {
    margin-top: -60px;
    padding-inline: 10px;
  }

  .project-hero-media__tags span {
    font-size: 11px;
    padding: 4px 7px;
  }

  .project-hero-media__cover-action.v-btn {
    max-width: calc(100% - 10px);
  }
}

@media (max-width: 395px) {
  .project-hero-media__body,
  .project-hero-media--page-sizing .project-hero-media__body {
    grid-template-columns: minmax(116px, 42vw);
  }

  .project-hero-media__content p {
    -webkit-line-clamp: 2;
  }
}

@media (max-width: 375px) {
  .project-hero-media__banner {
    min-height: 124px;
  }

  .project-hero-media__body,
  .project-hero-media--page-sizing .project-hero-media__body {
    margin-top: -54px;
  }
}

@media (max-width: 320px) {
  .project-hero-media__top {
    align-items: flex-start;
    flex-direction: column;
  }

  .project-hero-media__banner-action.v-btn {
    max-width: 100%;
  }

  .project-hero-media__body,
  .project-hero-media--page-sizing .project-hero-media__body {
    grid-template-columns: 120px;
  }
}
</style>
